import { rememberCompletionReturn } from "../../utils/profileCompletionReturn";
import { hasMeetingLocation } from "../../utils/meetingLocationSelection";
import MeetingLocationPicker from "../../components/meetingLocation/MeetingLocationPicker";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowDown,
  Bone,
  MapPin,
  Mail,
  X,
  ChevronDown,
  Loader2,
  Heart,
} from "lucide-react";
import "./ProfileDetailsPage.css";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import tablerBone from "../../assets/tabler_bone.png";
import morningIcon from "../../assets/availability1.png";
import afternoonIcon from "../../assets/availability2.png";
import eveningIcon from "../../assets/availability3.png";
import weekedaysIcon from "../../assets/availability4.png";
import weekenedsIcon from "../../assets/availability5.png";
import vacationIcon from "../../assets/availability6.png";
import home from "../../assets/home.svg";
import gender from "../../assets/gender.svg";
import cake from "../../assets/cake.svg";
import pawIcon2 from "../../assets/pawIcon2_1.svg";
import greenPawIcon from "../../assets/greenPawIcon.svg";
import d2d from "../../assets/greenIconExpWithDogs.svg";
import d2h from "../../assets/greenIconExpWithHumans.svg";
import profileAvailability from "../../assets/greenCalendar.svg";
import pawIcon3 from "../../assets/pawIcon3.svg";
import dog5 from "../../assets/dog5.svg";
import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endPoints";
import appIcon from "../../assets/appIcon.svg";
import MobileTopBar from "../../components/MobileTopBar";

import {
  collection,
  setDoc,
  addDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase-config";
import { useGlobalAlert } from "../../context/AlertContext";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";

const loadedImageCache = new Set();

const ImageWithPlaceholder = ({
  src,
  placeholder,
  alt,
  className,
  style,
  onLoadCallback,
}) => {
  const [isLoading, setIsLoading] = useState(() => {
    return src && !loadedImageCache.has(src);
  });
  const [imgSrc, setImgSrc] = useState(() => {
    return loadedImageCache.has(src) ? src : placeholder;
  });

  useEffect(() => {
    if (!src) {
      setImgSrc(placeholder);
      setIsLoading(false);
      return;
    }

    if (loadedImageCache.has(src)) {
      setImgSrc(src);
      setIsLoading(false);
      if (onLoadCallback) onLoadCallback();
      return;
    }

    setImgSrc(placeholder);
    setIsLoading(true);
    let isMounted = true;

    const img = new Image();
    img.src = src;

    img.onload = () => {
      if (isMounted) {
        loadedImageCache.add(src);
        setImgSrc(src);
        setIsLoading(false);
        if (onLoadCallback) onLoadCallback();
      }
    };

    img.onerror = () => {
      if (isMounted) {
        setImgSrc(placeholder);
        setIsLoading(false);
      }
    };

    return () => {
      isMounted = false;
    };
  }, [src, placeholder, onLoadCallback]);

  return (
    <>
      <img
        src={imgSrc}
        alt={alt}
        className={className}
        style={{
          ...style,
          opacity: isLoading ? 0.5 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <Loader2 className="animate-spin" size={32} color="#005b7d" />
        </div>
      )}
    </>
  );
};

const calculateDistanceMiles = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 3958.8;
  const rLat1 = lat1 * (Math.PI / 180);
  const rLat2 = lat2 * (Math.PI / 180);
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const formatDistance = (miles = 0) => {
  const blocks = Math.round(miles * 20);
  if (blocks <= 20) {
    return `${blocks} ${blocks === 1 ? "block" : "blocks"} away`;
  }
  const roundedMiles = Number(miles.toFixed(1));
  return `${roundedMiles} ${roundedMiles === 1 ? "mile" : "miles"} away`;
};

const getDynamicDateLabel = (date) => {
  if (!date) return "FLEXIBLE";
  const target = dayjs(date);
  const today = dayjs();

  if (target.isSame(today, "day")) return "TODAY";
  if (target.isSame(today.add(1, "day"), "day")) return "TOMORROW";

  return "DATE";
};

const getDynamicTimeLabel = (date) => {
  if (!date) return "TIME";
  const hour = dayjs(date).hour();

  if (hour >= 0 && hour < 12) return "MORNING";
  if (hour >= 12 && hour < 17) return "AFTERNOON";

  return "EVENING";
};

const ProfileDetailsPage = () => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();

  const location = useLocation();
  const { profile, loading } = useSelector((state) => state.user);

  const [dogData, setDogData] = useState(location.state?.dogData || null);
  const [activeView, setActiveView] = useState(
    location.state?.activeView || "dog",
  );

  const dog = dogData;
  const shareParams = new URLSearchParams(location.search);
  const pId = shareParams.get("profileId") || location.state?.pId;
  const sharedDogId = shareParams.get("dogId");
  const isSocial =
    (dog?.team_social === 1 && dog?.team_paws !== 1) ||
    dog?.user_dog_status === 0;

  const [neighborhood, setNeighborhood] = useState("Neighborhood");
  const [loadingDetails, setLoadingDetails] = useState(!location.state?.dogData);
  const [profileLoadError, setProfileLoadError] = useState("");

  const timeDropdownRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const rightPanelRef = useRef(null);

  const getFirstName = (fullName) => {
    if (!fullName) return "";
    return fullName.trim().split(" ")[0];
  };

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  useEffect(() => {
    if (location.state?.dogData && !new URLSearchParams(location.search).get("profileId") && !sharedDogId) {
      setDogData(location.state.dogData);
      setProfileLoadError("");
      setLoadingDetails(false);
      return;
    }
    let cancelled = false;
    const loadProfile = async () => {
      setLoadingDetails(true);
      setProfileLoadError("");
      setDogData(null);
      try {
        let userData = {};
        let primaryDog = {};
        if (sharedDogId) {
          const res = await apiClient.get(`/dogs/${encodeURIComponent(sharedDogId)}`, { timeout: 12000 });
          if (cancelled) return;
          primaryDog = res.data;
          if (!primaryDog || String(primaryDog.id) !== sharedDogId || !primaryDog.user_id) {
            throw new Error("This dog profile is not available.");
          }
          // The dog endpoint is authoritative for which owner to load.
          const owner = await apiClient.get(`/user/details/${encodeURIComponent(primaryDog.user_id)}`, {
            timeout: 8000,
          }).catch(() => null);
          if (cancelled) return;
          userData = owner?.data?.status && owner.data.data
            ? owner.data.data
            : { id: primaryDog.user_id };
        } else {
          if (!pId) throw new Error("This link is missing the profile ID.");
          const res = await apiClient.get(`/user/details/${encodeURIComponent(pId)}`, { timeout: 12000 });
          if (cancelled) return;
          if (!res.data?.status || !res.data.data) throw new Error("This profile is not available.");
          userData = res.data.data;
          primaryDog = userData.dogs?.[0] || {};
        }
            let distance_miles = 0;
            const savedLocStr = localStorage.getItem("user_location_coords");

            if (
              savedLocStr &&
              userData.user_latitude &&
              userData.user_longitude
            ) {
              try {
                const savedLoc = JSON.parse(savedLocStr);
                distance_miles = calculateDistanceMiles(
                  savedLoc.lat,
                  savedLoc.lng,
                  parseFloat(userData.user_latitude),
                  parseFloat(userData.user_longitude),
                );
              } catch (e) {
                console.error(
                  "Failed to parse location or calculate distance",
                  e,
                );
              }
            }

            const getValidImage = (arr) =>
              Array.isArray(arr) && arr.some((img) => img) ? arr : null;

            const normalizedData = {
              ...userData,
              ...primaryDog,
              user_id: userData.id,
              profile_image:
                getValidImage(userData.user_profile_image) ||
                getValidImage(userData.profile_image) ||
                getValidImage(primaryDog.profile_image) ||
                [],
              team_social: primaryDog.team_social ?? (Number(userData.user_dog_status) === 1 ? 0 : 1),
              distance_miles: distance_miles,
            };

            setDogData(normalizedData);
            // Follow the same state-based navigation as the map's View Profile button.
            // Remove the query so the destination renders the supplied data without refetching.
            if (sharedDogId) {
              navigate("/profile-details", {
                replace: true,
                state: { dogData: normalizedData },
              });
            }
      } catch (error) {
        if (!cancelled) setProfileLoadError(error.response?.status === 404
          ? "This profile is no longer available."
          : "Unable to load this profile. Please check your connection and try again.");
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    };
    loadProfile();
    return () => { cancelled = true; };
  }, [pId, sharedDogId, location.state?.dogData, location.search, navigate]);

  useEffect(() => {
    if (!isMapLoaded || !dog) return;

    if (dog.neighborhood && dog.neighborhood.trim() !== "") {
      setNeighborhood(dog.neighborhood);
      return;
    }

    const fetchNeighborhoodData = async () => {
      if (dog.user_latitude && dog.user_longitude) {
        const lat = parseFloat(dog.user_latitude);
        const lng = parseFloat(dog.user_longitude);

        try {
          const aiRes = await apiClient.post("/openAI/get-neighborhood-by-AI", {
            latitude: lat,
            longitude: lng,
          });

          if (aiRes.data?.status && aiRes.data?.data?.neighborhood) {
            const aiNeigh = aiRes.data.data.neighborhood;
            const lower = aiNeigh.toLowerCase();

            if (
              !lower.includes("sorry") &&
              !lower.includes("not recognizable")
            ) {
              setNeighborhood(aiNeigh);
              return;
            }
          }
        } catch (err) {
          console.error("AI neighborhood fetch failed, using fallback", err);
        }

        const geocoder = new window.google.maps.Geocoder();
        const latlng = { lat, lng };

        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === "OK" && results[0]) {
            const cityComponent = results[0].address_components.find(
              (component) =>
                // component.types.includes("locality") ||
                component.types.includes("sublocality") ||
                component.types.includes("neighborhood"),
            );

            if (cityComponent && cityComponent.long_name) {
              setNeighborhood(cityComponent.long_name);
            } else if (dog.user_city && dog.user_city.trim() !== "") {
              setNeighborhood(dog.user_city);
            } else {
              setNeighborhood(results[0].formatted_address);
            }
          } else {
            console.error("Geocoder failed due to:", status);
            if (dog.user_city && dog.user_city.trim() !== "") {
              setNeighborhood(dog.user_city);
            }
          }

        });
      } else {
        if (dog.user_city && dog.user_city.trim() !== "") {
          setNeighborhood(dog.user_city);
        }
        }
    };

    fetchNeighborhoodData();
  }, [isMapLoaded, dog]);

  const displayData = useMemo(() => {
    if (!dog) return null;

    const toTitleCase = (value) => {
      if (!value || (typeof value === "string" && value.trim() === "")) {
        return null;
      }

      if (typeof value !== "string") return value;

      return value
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const joinIfArray = (value) => {
      if (!value) return null;

      if (Array.isArray(value)) {
        return value.length ? value.join(", ") : null;
      }

      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed.length ? parsed.join(", ") : null;
          }
          return value.trim() === "" ? null : value;
        } catch {
          return value.trim() === "" ? null : value;
        }
      }

      return value;
    };

    const getPersonalityArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === "object") return Object.values(val);
      if (typeof val === "string") {
        try {
          const parsed = JSON.parse(val);
          if (Array.isArray(parsed)) return parsed;
          if (typeof parsed === "object") return Object.values(parsed);
        } catch {
          return val
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
        }
      }
      return [];
    };

    const getImagesArray = (imgData) => {
      if (!imgData) return [];
      let arr = [];
      if (Array.isArray(imgData)) {
        arr = imgData;
      } else if (typeof imgData === "string") {
        try {
          const parsed = JSON.parse(imgData);
          if (Array.isArray(parsed)) arr = parsed;
        } catch {
          arr = imgData.trim() !== "" ? [imgData] : [];
        }
      }
      return arr.filter(
        (img) => img !== null && img !== undefined && img !== "",
      );
    };

    const isTeamPaws =
      dogData?.user_dog_status === 1 ||
      dogData?.team_paws === 1 ||
      dog?.team_paws === 1;

    const getFirstValidImageArray = (...sources) => {
      for (const source of sources) {
        const arr = getImagesArray(source);
        if (arr.length > 0) return arr;
      }
      return [];
    };

    const parsedDogImages = getFirstValidImageArray(
      dog?.dog_images,
      dogData?.dog_images,
    );

    const parsedUserImages = getFirstValidImageArray(
      dogData?.user_profile_image,
      dogData?.profile_image,
      dog?.user_profile_image,
      dog?.profile_image,
    );

    let finalImages = [];
    if (isTeamPaws) {
      if (activeView === "user") {
        if (parsedUserImages.length > 0 && parsedUserImages[0].trim() !== "") {
          finalImages = parsedUserImages;
        } else {
          finalImages = [userPlaceholder];
        }
      } else {
        if (parsedDogImages.length > 0 && parsedDogImages[0].trim() !== "") {
          finalImages = parsedDogImages;
        } else if (
          parsedUserImages.length > 0 &&
          parsedUserImages[0].trim() !== ""
        ) {
          finalImages = parsedUserImages;
        } else {
          finalImages = [dogPlaceholder];
        }
      }
    } else {
      if (parsedUserImages.length > 0 && parsedUserImages[0].trim() !== "") {
        finalImages = parsedUserImages;
      } else {
        finalImages = [userPlaceholder];
      }
    }

    if (isSocial) {
      const availSocial = joinIfArray(dog?.availability);

      return {
        name: dog.user_name || "Pup Pal",
        userName: dog.user_name || "Pup Pal",
        brings_me_here:
          dog?.whats_brings_you || dog?.brings_you_here
            ? toTitleCase(
                joinIfArray(dog?.whats_brings_you || dog?.brings_you_here),
              )
            : null,
        title_icon: <Heart size={20} fill="#fff" stroke="none" />,
        vitals: [
          {
            label: "Bio",
            value: "Pup Pal",
            icon: <img src={gender} alt="gender" />,
          },
          {
            label: "Goal",
            value: "Social Connection",
            icon: <img src={pawIcon3} alt="pawIcon3" />,
          },
          {
            label: "Activity",
            value: toTitleCase(neighborhood),
            icon: <img src={home} alt="home" />,
          },
        ].filter((v) => v.value),
        attributes: [
          {
            label: "Bio",
            value: dog?.user_bio ? (
              <b style={{ fontWeight: "700" }}>{dog.user_bio}</b>
            ) : null,
            icon: pawIcon2,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Activity",
            value: dog?.dog_activity ? (
              <>
                I want to spend time with a pup for{" "}
                <b style={{ fontWeight: "700" }}>
                  {(() => {
                    const activity = dog.dog_activity?.toLowerCase();

                    if (activity === "both") {
                      return "Companionship and Experience";
                    }
                    if (activity === "companionship") {
                      return "Companionship";
                    }
                    if (activity === "experience") {
                      return "Experience";
                    }
                    return toTitleCase(dog.dog_activity);
                  })()}
                </b>
              </>
            ) : null,
            icon: pawIcon2,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Availability",
            value: availSocial ? (
              <>
                I'm generally{" "}
                <b style={{ fontWeight: "700" }}>
                  available in {toTitleCase(availSocial)} for walks or playdates
                </b>
              </>
            ) : null,
            icon: profileAvailability,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Experience",
            value: dog?.past_experience_with_dogs ? (
              <>
                In my past experience{" "}
                <b style={{ fontWeight: "700" }}>
                  {dog.past_experience_with_dogs}
                </b>
              </>
            ) : null,
            icon: d2h,
            width: window.innerWidth <= 1023 ? "22px" : "26px",
          },
        ].filter((a) => a.value !== null),
        images: finalImages,
        tagIcon: (
          <img
            src={pawIcon2}
            alt="pawIcon2"
            style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
          />
        ),
        personalityList: getPersonalityArray(
          dog?.pawsonality_check || dog?.personality,
        )
          .map(toTitleCase)
          .filter(Boolean),
      };
    } else {
      let displayAge = null;

      if (dog.age_group && dog.age_group.trim() !== "") {
        displayAge = dog.age_group.split("/")[0].trim();
      } else if (dog.dog_birthday && dog.dog_birthday.trim() !== "") {
        const birthDate = new Date(dog.dog_birthday);
        const today = new Date();

        const diffTime = today - birthDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        const years = Math.floor(diffDays / 365.25);
        const months = Math.floor(diffDays / 30.44);

        if (years >= 1) {
          displayAge = `${years} ${years === 1 ? "Year" : "Years"} Old`;
        } else {
          displayAge = `${months} ${months === 1 ? "Month" : "Months"} Old`;
        }
      }

      const genderStr =
        dog.dog_gender === "Male"
          ? "Boy"
          : dog.dog_gender === "Female"
            ? "Girl"
            : "";
      let displayBreed = null;

      if (dog.dog_breed) {
        if (
          dog.dog_breed.toLowerCase() === "mixed breed" &&
          Array.isArray(dog.dog_mixedBreed) &&
          dog.dog_mixedBreed.length > 0
        ) {
          const mixedStr = dog.dog_mixedBreed.join(", ");
          displayBreed = toTitleCase(`${dog.dog_breed}, ${mixedStr}`);
        } else {
          displayBreed = toTitleCase(dog.dog_breed);
        }
      }

      const genderAndBreed = [genderStr, displayBreed]
        .filter(Boolean)
        .join(" ");

      let activityText = null;
      if (dog.dog_activity) {
        const act = dog.dog_activity.toLowerCase();
        if (act === "both") activityText = "Companionship and Exercise";
        else if (act === "companionship") activityText = "Companionship";
        else if (act === "exercise") activityText = "Exercise";
        else activityText = toTitleCase(dog.dog_activity);
      }

      const availStr = joinIfArray(dog.availability);
      const availabilityText = availStr
        ? `available in ${availStr} for walks or playdates`
        : null;

      const dogAttributes = [
        {
          label: "Bio",
          value: dog?.bio ? toTitleCase(dog.bio) : null,
          icon: greenPawIcon,
          width: window.innerWidth <= 1023 ? "24px" : "28px",
        },
        {
          label: "Experience with other dogs",
          value: toTitleCase(
            dog?.past_experience_with_dogs ||
              dog?.along_with_other_dogs ||
              null,
          ),
          icon: d2d,
          width: window.innerWidth <= 1023 ? "26px" : "30px",
        },
        {
          label: "Experience with humans",
          value: toTitleCase(dog?.along_with_other_humans || null),
          icon: d2h,
          width: window.innerWidth <= 1023 ? "22px" : "26px",
        },
        {
          label: "Availability",
          value: toTitleCase(availabilityText),
          icon: profileAvailability,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
      ].filter((attr) => attr.value !== null);

      const bringsYouHereFormatted =
        dog?.whats_brings_you || dog?.brings_you_here
          ? toTitleCase(
              joinIfArray(dog?.whats_brings_you || dog?.brings_you_here),
            )
          : null;

      const userAttributes = [
        {
          label: "Brings Me Here",
          value: bringsYouHereFormatted ? (
            <>
              <b style={{ fontWeight: "700" }}>I'm here because </b>
              {bringsYouHereFormatted}
            </>
          ) : null,
          icon: pawIcon2,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
        {
          label: "Bio",
          value: dog?.user_bio ? toTitleCase(dog.user_bio) : null,
          icon: pawIcon2,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
      ].filter((attr) => attr.value !== null);

      if (activeView === "user") {
        return {
          name: dog.dog_name || "Your Pup",
          userName: dog.user_name || "User",
          title_icon: <Bone size={20} fill="#fff" stroke="none" />,
          vitals: [],
          attributes: userAttributes,
          images: finalImages,
          tagIcon: (
            <img
              src={pawIcon2}
              alt="pawIcon2"
              style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
            />
          ),
          brings_me_here:
            dog?.whats_brings_you || dog?.brings_you_here
              ? toTitleCase(
                  joinIfArray(dog?.whats_brings_you || dog?.brings_you_here),
                )
              : null,
        };
      } else {
        return {
          name: dog.dog_name || "Your Pup",
          userName: dog.user_name || "User",
          genderAndBreed: genderAndBreed ? genderAndBreed : null,
          activityText: activityText,
          availabilityText: availabilityText,
          traitsList: getPersonalityArray(dog.dog_traits)
            .map(toTitleCase)
            .filter(Boolean),
          hobbiesList: getPersonalityArray(dog.dog_hobbies)
            .map(toTitleCase)
            .filter(Boolean),
          brings_me_here:
            dog?.whats_brings_you || dog?.brings_you_here
              ? toTitleCase(
                  joinIfArray(dog?.whats_brings_you || dog?.brings_you_here),
                )
              : null,
          title_icon: <Bone size={20} fill="#fff" stroke="none" />,
          vitals: [
            {
              label: "Age",
              value: toTitleCase(displayAge),
              icon: <img src={cake} alt="cake" />,
            },
            {
              label: "Gender",
              value: toTitleCase(
                dog.dog_gender === "Male"
                  ? "Boy"
                  : dog.dog_gender === "Female"
                    ? "Girl"
                    : null,
              ),
              icon: <img src={gender} alt="gender" />,
            },
            {
              label: "Breed",
              value: toTitleCase(dog.dog_breed),
              icon: <img src={pawIcon3} alt="pawIcon3" />,
            },
            {
              label: "Activity",
              value: toTitleCase(neighborhood),
              icon: <img src={home} alt="home" />,
            },
          ].filter((v) => v.value),
          attributes: dogAttributes,
          images: finalImages,
          bio: dog.bio && dog.bio.trim() !== "" ? toTitleCase(dog.bio) : null,
          tagIcon: (
            <img
              src={pawIcon2}
              alt="pawIcon2"
              style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
            />
          ),
          personalityList: getPersonalityArray(
            dog.personality || dog.pawsonality_check,
          )
            .map(toTitleCase)
            .filter(Boolean),
        };
      }
    }
  }, [dog, dogData, isSocial, neighborhood, activeView]);

  const [treatDog, setTreatDog] = useState(null);
  const [showTreatModal, setShowTreatModal] = useState(false);
  const [showBookWalkModal, setShowBookWalkModal] = useState(false);
  const [walkTab, setWalkTab] = useState("calendar");
  const [selectedFlexibleOptions, setSelectedFlexibleOptions] = useState([]);
  const [showWalkConfirmModal, setShowWalkConfirmModal] = useState(false);
  const [showMeetingPointModal, setShowMeetingPointModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [walkMessage, setWalkMessage] = useState("");
  const [isTossingTreat, setIsTossingTreat] = useState(false);
  const [isCreatingWalk, setIsCreatingWalk] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [value, setValue] = useState(dayjs());
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [isBouncing, setIsBouncing] = useState(false);
  const [canScroll, setCanScroll] = useState(false);

  const [selectedMeetingAddress, setSelectedMeetingAddress] = useState("");
  const [selectedMeetingLocation, setSelectedMeetingLocation] = useState(null);
  const [userLocation] = useState({
    lat: 40.7648,
    lng: -73.9808,
  });
  const [showIncompleteProfileModal, setShowIncompleteProfileModal] =
    useState(false);

  const handleScrollLogic = useCallback(() => {
    const isMobile = window.innerWidth <= 767;
    const container = isMobile
      ? scrollContainerRef.current
      : rightPanelRef.current;

    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    const isScrollable = scrollHeight > clientHeight + 1;
    const hasMoreToScroll = scrollTop + clientHeight < scrollHeight - 10;

    setCanScroll(isScrollable);
    setIsBouncing(hasMoreToScroll);
    setIsAtBottom(!hasMoreToScroll);
  }, []);

  useEffect(() => {
    const desktopPanel = rightPanelRef.current;
    const mobileContainer = scrollContainerRef.current;

    handleScrollLogic();

    if (desktopPanel)
      desktopPanel.addEventListener("scroll", handleScrollLogic);
    if (mobileContainer)
      mobileContainer.addEventListener("scroll", handleScrollLogic);
    window.addEventListener("resize", handleScrollLogic);

    return () => {
      if (desktopPanel)
        desktopPanel.removeEventListener("scroll", handleScrollLogic);
      if (mobileContainer)
        mobileContainer.removeEventListener("scroll", handleScrollLogic);
      window.removeEventListener("resize", handleScrollLogic);
    };
  }, [displayData, handleScrollLogic]);

  const handleScrollClick = () => {
    const isMobile = window.innerWidth <= 767;
    const container = isMobile
      ? scrollContainerRef.current
      : rightPanelRef.current;

    if (!container) return;

    if (!isBouncing) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      container.scrollBy({ top: 300, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    localStorage.setItem("dashboard_current_view", "map");
    navigate("/dashboard-switcher", { replace: true, state: { view: "map" } });
  };

  const handleGotoMessage = () => {
    if (!checkCompletionStatus()) return;

    navigate("/messages", {
      state: { partnerId: dog?.user_id },
    });
  };

  const checkCompletionStatus = () => {
    const isComplete = profile?.dogs?.[0]?.isDogProfileComplete;
    const isHalfComplete = profile?.dogs?.[0]?.isDogProfileHalfComplete;

    if (isComplete === 1) {
      return true;
    }

    if (isComplete === 0 && isHalfComplete === 1) {
      rememberCompletionReturn(profile?.id, { dogData: dog, activeView });
      navigate("/half-completed-page");
      return false;
    }

    setShowIncompleteProfileModal(true);
    return false;
  };

  const handleTossTreat = async (dog) => {
    if (!checkCompletionStatus()) return;

    if (!profile?.id || !dog?.user_id) {
      triggerAlert("User or Dog information missing", "error");
      return;
    }

    setIsTossingTreat(true);

    try {
      const payload = {
        sender_user_id: profile?.id,
        receiver_user_id: dog?.user_id,
        receiver_dog_id: dog?.id,
      };

      const response = await apiClient.post(
        ENDPOINTS.CREATE_WEBAPP_TREAT,
        payload,
      );

      if (response.data?.status) {
        setTreatDog(dog);
        setShowTreatModal(true);
      } else {
        triggerAlert(
          response.data?.message || "Failed to toss a treat.",
          "error",
        );
      }
    } catch (error) {
      console.error("Error tossing treat:", error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          `Server Error: ${error.response.status}`;
        triggerAlert(errorMessage, "error");
      } else if (error.request) {
        triggerAlert(
          "No response from server. Please check your internet connection.",
          "error",
        );
      } else {
        triggerAlert("An unexpected error occurred.", "error");
      }
    } finally {
      setIsTossingTreat(false);
    }
  };

  const handleBookWalk = () => {
    if (!checkCompletionStatus()) return;
    setTreatDog(dog);
    setShowBookWalkModal(true);
  };

  const handleCloseModal = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setTreatDog(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const toggleFlexibleOption = (option) => {
    if (selectedFlexibleOptions.includes(option)) {
      setSelectedFlexibleOptions((prev) => prev.filter((o) => o !== option));
    } else {
      setSelectedFlexibleOptions((prev) => [...prev, option]);
    }
  };

  const TIME_SLOTS = useMemo(() => {
    const interval = 30;
    const slots = [];
    const now = dayjs();
    const isToday = value && value.isSame(now, "day");

    const currentMinute = now.minute();
    const addMinutes = interval - (currentMinute % interval);
    const nextAvailableTime = now
      .clone()
      .add(addMinutes, "minute")
      .second(0)
      .millisecond(0);

    for (let h = 6; h <= 21; h++) {
      for (let m = 0; m < 60; m += interval) {
        if (h === 21 && m > 0) break;

        const slotTime = (value || now)
          .clone()
          .hour(h)
          .minute(m)
          .second(0)
          .millisecond(0);

        if (isToday) {
          if (
            slotTime.isSame(nextAvailableTime) ||
            slotTime.isAfter(nextAvailableTime)
          ) {
            slots.push(slotTime.format("hh:mm A"));
          }
        } else {
          slots.push(slotTime.format("hh:mm A"));
        }
      }
    }

    return slots;
  }, [value]);

  const handleFlexibleSubmit = () => {
    if (selectedFlexibleOptions.length === 0) return;
    setSelectedDate(null);
    setSelectedTime(null);
    openMeetingPoint();
  };

  const openMeetingPoint = () => {
    setShowBookWalkModal(false);
    setShowMeetingPointModal(true);
  };

  const backToBookWalk = () => {
    setShowMeetingPointModal(false);
    setShowBookWalkModal(true);
  };

  const openWalkConfirm = () => {
    if (!hasMeetingLocation(selectedMeetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }
    setShowMeetingPointModal(false);
    setShowWalkConfirmModal(true);
  };

  const backToMeetingPoint = () => {
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(true);
  };

  const closeAllModalsFully = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(false);
    setShowSuccessModal(false);
    setTreatDog(null);
    setSelectedMeetingAddress("");
    setSelectedMeetingLocation(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
    setWalkMessage("");
  };

  const closeAllModals = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowSuccessModal(false);
    setTreatDog(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
    setWalkMessage("");
  };

  const handleCreateWalk = async () => {
    if (!profile?.id || !treatDog) {
      triggerAlert("Missing user or dog profile information.", "error");
      return;
    }

    if (!hasMeetingLocation(selectedMeetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }

    setIsCreatingWalk(true);

    try {
      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const isTreatDogSocial =
        (treatDog.team_social === 1 && treatDog.team_paws !== 1) ||
        treatDog.user_dog_status === 0;

      const effectiveDogId = isTreatDogSocial
        ? profile?.dogs?.[0]?.id
        : treatDog.id;

      const payload = {
        requester_user_id: profile.id,
        walker_user_id: treatDog.user_id,
        dog_id: effectiveDogId,
        date: selectedDate ? selectedDate.toISOString() : null,
        availability: walkTab === "flexible" ? selectedFlexibleOptions : [],
        meeting_location: selectedMeetingAddress.trim(),
        timezone: deviceTimezone,
        sender_offer_message: walkMessage,
        receiver_offer_message: "",
      };

      const response = await apiClient.post(ENDPOINTS.CREATE_A_WALK, payload);

      if (response.data?.status) {
        triggerAlert("Walk request sent successfully!", "success");

        if (walkMessage && walkMessage.trim() !== "") {
          try {
            const currentUserId = String(profile.id);
            const partnerId = String(treatDog.user_id);
            const chatDocId = [currentUserId, partnerId].sort().join("_");
            const chatRef = doc(db, "web_chat_list", chatDocId);
            const messagesRef = collection(chatRef, "messages");
            const newMsgRef = doc(messagesRef);

            await setDoc(
              chatRef,
              {
                users: [currentUserId, partnerId],
                last_message: walkMessage,
                last_timestamp: serverTimestamp(),
              },
              { merge: true },
            );

            await setDoc(newMsgRef, {
              sender_id: currentUserId,
              receiver_id: partnerId,
              message: walkMessage,
              file: null,
              timestamp: serverTimestamp(),
              is_read: false,
            });
          } catch (chatErr) {
            console.error("Failed to add walk message to chat:", chatErr);
          }
        }

        closeAllModalsFully();
      } else {
        triggerAlert(
          response.data?.message || "Failed to create walk request.",
          "error",
        );
      }
    } catch (error) {
      console.error("Create Walk Error:", error);
      triggerAlert("Something went wrong. Please try again.", "error");
    } finally {
      setIsCreatingWalk(false);
    }
  };

  const handleScrollToTime = () => {
    const isMobile =
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
      (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);

    if (!isMobile) return;

    setShowTimeDropdown(true);
    setTimeout(() => {
      timeDropdownRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  };

  const handleCalendarClick = (e) => {
    if (e.target.closest(".MuiPickersDay-root")) {
      handleScrollToTime();
    }
  };

  if (loadingDetails) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  if (profileLoadError || !dogData) {
    return <div className="profile-detail-container" style={{ padding: "3rem", color: "white" }}>
      <h2>Profile unavailable</h2>
      <p role="alert">{profileLoadError || "This profile could not be found."}</p>
      <button onClick={() => window.location.reload()}>Try again</button>{" "}
      <button onClick={handleBack}>Back to map</button>
    </div>;
  }

  const feedImages = displayData?.images?.slice(1) || [];

  return (
    <div className="profile-detail-container" ref={scrollContainerRef}>
      <MobileTopBar onBack={handleBack} backColor="#fff" logoColor="#fff" />

      {/* Header: Back Button (Desktop) */}
      <div className="profile-header pdp-desktop-only">
        <button
          type="button"
          onClick={handleBack}
          aria-label="Back to map"
          style={{ background: "transparent", border: 0, color: "white", cursor: "pointer", marginRight: "12px" }}
        >
          <ArrowLeft size={24} />
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
          }}
          onClick={handleBack}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "28px", height: "28px", objectFit: "contain" }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: "22px",
              fontWeight: "700",
            }}
          >
            Social Paws
          </span>
        </div>
      </div>

      {/* LEFT PANEL (FIXED SUMMARY) */}
      <div className="detail-left-panel">
        <div className="left-panel-wrapper">
          {!isSocial && (
            <div className="parent-names-header">
              <span
                onClick={() => setActiveView("dog")}
                style={{
                  cursor: "pointer",
                  opacity: activeView === "dog" ? 1 : 0.7,
                  fontWeight: activeView === "dog" ? "bold" : "normal",
                }}
              >
                <span className="pdp-desktop-inline">{displayData?.name}</span>
                <span className="pdp-mobile-inline">
                  {getFirstName(displayData?.name)}
                </span>
              </span>
              <span className="name-separator"></span>
              <span
                onClick={() => setActiveView("user")}
                style={{
                  cursor: "pointer",
                  opacity: activeView === "user" ? 1 : 0.7,
                  fontWeight: activeView === "user" ? "bold" : "normal",
                }}
              >
                <span className="pdp-desktop-inline">
                  {displayData?.userName}
                </span>
                <span className="pdp-mobile-inline">
                  {getFirstName(displayData?.userName)}
                </span>
              </span>
            </div>
          )}

          <div className="profile-summary-card">
            {/* Main Image */}
            <div className="main-image-container">
              <div className="image-relative-wrapper">
                <ImageWithPlaceholder
                  src={displayData.images[0]}
                  placeholder={
                    isSocial || activeView === "user"
                      ? userPlaceholder
                      : dogPlaceholder
                  }
                  alt="Avatar"
                  className="main-dog-image"
                />
              </div>
              <div className="image-overlay-text">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    overflowWrap: "anywhere",
                  }}
                >
                  {displayData.title_icon}
                  <span className="dog-name-overlay">
                    {activeView === "user" ? (
                      <>
                        <span className="pdp-desktop-inline">
                          {getFirstName(displayData.userName)}
                        </span>
                        <span className="pdp-mobile-inline">
                          {getFirstName(displayData.userName)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="pdp-desktop-inline">
                          {getFirstName(
                            displayData.name || displayData.userName,
                          )}
                        </span>
                        <span className="pdp-mobile-inline">
                          {getFirstName(
                            displayData.name || displayData.userName,
                          )}
                        </span>
                      </>
                    )}
                  </span>
                </div>
                <span className="distance-overlay">
                  {formatDistance(dog?.distance_miles)}
                </span>
              </div>
            </div>

            {/* Dynamically show vitals row if data exists */}
            {displayData.vitals &&
              displayData.vitals.length > 0 &&
              !isSocial && (
                <div className="vitals-row">
                  {displayData.vitals.map((v, i) => (
                    <div key={i} className="vital-item">
                      {React.cloneElement(v.icon, {
                        style: { stroke: "rgba(68, 138, 123, 1)" },
                        strokeWidth: 3,
                        color: "transparent",
                        width: window.innerWidth <= 1023 ? "16px" : "22px",
                      })}
                      {v.value}
                    </div>
                  ))}
                </div>
              )}

            {/* Tags / Description List */}
            {displayData.attributes?.length > 0 && (
              <div className="profile-attributes">
                {displayData.attributes.map((attr, i) => (
                  <div key={i} className="attr-row">
                    <div className="attr-icon-wrapper">
                      <img
                        src={attr.icon}
                        alt={attr.label}
                        style={{
                          width: attr.width || "32px",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                    <span className="attr-text">{attr.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="profile-action-btns-new">
              <button
                className="profile-btn-full"
                disabled={isTossingTreat}
                onClick={() => handleTossTreat(dog)}
              >
                {isTossingTreat ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  "TOSS A TREAT"
                )}
              </button>
              <div className="profile-btn-row">
                <button className="profile-btn-half" onClick={handleBookWalk}>
                  OFFER A WALK
                </button>
                <button
                  className="profile-btn-half"
                  onClick={handleGotoMessage}
                >
                  SEND A MESSAGE
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL (SCROLLABLE FEED) */}
      <div
        className="detail-right-panel"
        ref={rightPanelRef}
        style={{ overflowY: "auto", maxHeight: "100dvh" }}
      >
        <div className="feed-container">
          <div className="bio-wrapper"></div>

          {isSocial ? (
            <>
              {/* SUB PHOTO 1 */}
              {feedImages.length > 0 && (
                <div className="feed-card photo-card">
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "330px",
                      margin: "0 auto",
                    }}
                  >
                    <ImageWithPlaceholder
                      src={feedImages[0]}
                      placeholder={userPlaceholder}
                      alt="Feed item 1"
                      onLoadCallback={handleScrollLogic}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: 16,
                        objectFit: "cover",
                      }}
                    />
                    <div className="feed-gradient-top" />
                    <div className="feed-gradient-bottom" />
                  </div>
                </div>
              )}

              {/* PAWSONALITY CHECK */}
              {displayData.personalityList &&
                displayData.personalityList.length > 0 && (
                  <div className="bio-wrapper">
                    <span className="tag-icon">{displayData.tagIcon}</span>
                    <div className="feed-card text-card">
                      {displayData.personalityList.map((trait, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom:
                              index === displayData.personalityList.length - 1
                                ? 0
                                : "8px",
                          }}
                        >
                          <p className="bio-text" style={{ margin: 0 }}>
                            <b style={{ fontWeight: "700" }}>{trait}</b>
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* SUB PHOTO 2 */}
              {feedImages.length > 1 && (
                <div className="feed-card photo-card">
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "330px",
                      margin: "0 auto",
                    }}
                  >
                    <ImageWithPlaceholder
                      src={feedImages[1]}
                      placeholder={userPlaceholder}
                      alt="Feed item 2"
                      onLoadCallback={handleScrollLogic}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: 16,
                        objectFit: "cover",
                      }}
                    />
                    <div className="feed-gradient-top" />
                    <div className="feed-gradient-bottom" />
                  </div>
                </div>
              )}

              {/* BRINGS ME HERE TEXT */}
              {displayData.brings_me_here && (
                <div className="bio-wrapper">
                  <span className="tag-icon">{displayData.tagIcon}</span>
                  <div className="feed-card text-card">
                    <p className="bio-text">
                      I'm here because{" "}
                      <b style={{ fontWeight: "700" }}>
                        {displayData.brings_me_here}
                      </b>
                    </p>
                  </div>
                </div>
              )}

              {/* REMAINING PHOTOS */}
              {feedImages.slice(2).map((imgUrl, index) => (
                <React.Fragment key={index}>
                  <div className="feed-card photo-card">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "330px",
                        margin: "0 auto",
                      }}
                    >
                      <ImageWithPlaceholder
                        src={imgUrl}
                        placeholder={userPlaceholder}
                        alt={`Feed item ${index + 3}`}
                        onLoadCallback={handleScrollLogic}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 16,
                          objectFit: "cover",
                        }}
                      />
                      <div className="feed-gradient-top" />
                      <div className="feed-gradient-bottom" />
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </>
          ) : activeView === "user" ? (
            <>
              {feedImages.map((imgUrl, index) => (
                <div
                  className="feed-card photo-card"
                  key={`user-feed-${index}`}
                >
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "330px",
                      margin: "0 auto",
                    }}
                  >
                    <ImageWithPlaceholder
                      src={imgUrl}
                      placeholder={userPlaceholder}
                      alt={`Feed item ${index + 1}`}
                      onLoadCallback={handleScrollLogic}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: 16,
                        objectFit: "cover",
                      }}
                    />
                    <div className="feed-gradient-top" />
                    <div className="feed-gradient-bottom" />
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {/* SUB PHOTO 1 */}
              {feedImages.length > 0 && (
                <div className="feed-card photo-card">
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "330px",
                      margin: "0 auto",
                    }}
                  >
                    <ImageWithPlaceholder
                      src={feedImages[0]}
                      placeholder={dogPlaceholder}
                      alt="Feed item 1"
                      onLoadCallback={handleScrollLogic}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: 16,
                        objectFit: "cover",
                      }}
                    />
                    <div className="feed-gradient-top" />
                    <div className="feed-gradient-bottom" />
                  </div>
                </div>
              )}

              {/* TRAITS LIST */}
              {displayData.traitsList && displayData.traitsList.length > 0 && (
                <div className="bio-wrapper">
                  <span className="tag-icon">{displayData.tagIcon}</span>
                  <div className="feed-card text-card">
                    <p className="bio-text" style={{ margin: 0 }}>
                      <b style={{ fontWeight: "700" }}>
                        {displayData.name} is a{" "}
                        {displayData.traitsList.join(", ")}
                      </b>
                    </p>
                  </div>
                </div>
              )}

              {/* SUB PHOTO 2 */}
              {feedImages.length > 1 && (
                <div className="feed-card photo-card">
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "330px",
                      margin: "0 auto",
                    }}
                  >
                    <ImageWithPlaceholder
                      src={feedImages[1]}
                      placeholder={dogPlaceholder}
                      alt="Feed item 2"
                      onLoadCallback={handleScrollLogic}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: 16,
                        objectFit: "cover",
                      }}
                    />
                    <div className="feed-gradient-top" />
                    <div className="feed-gradient-bottom" />
                  </div>
                </div>
              )}

              {/* HOBBIES LIST */}
              {displayData.hobbiesList &&
                displayData.hobbiesList.length > 0 && (
                  <div className="bio-wrapper">
                    <span className="tag-icon">{displayData.tagIcon}</span>
                    <div className="feed-card text-card">
                      <p className="bio-text" style={{ margin: 0 }}>
                        <b style={{ fontWeight: "700" }}>
                          {displayData.name} enjoys{" "}
                          {displayData.hobbiesList.join(", ")}
                        </b>
                      </p>
                    </div>
                  </div>
                )}

              {/* SUB PHOTO 3 */}
              {feedImages.length > 2 && (
                <div className="feed-card photo-card">
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "330px",
                      margin: "0 auto",
                    }}
                  >
                    <ImageWithPlaceholder
                      src={feedImages[2]}
                      placeholder={dogPlaceholder}
                      alt="Feed item 3"
                      onLoadCallback={handleScrollLogic}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: 16,
                        objectFit: "cover",
                      }}
                    />
                    <div className="feed-gradient-top" />
                    <div className="feed-gradient-bottom" />
                  </div>
                </div>
              )}

              {/* ACTIVITY TEXT */}
              {displayData.activityText && (
                <div className="bio-wrapper">
                  <span className="tag-icon">{displayData.tagIcon}</span>
                  <div className="feed-card text-card">
                    <p className="bio-text">
                      {displayData.name} is here for{" "}
                      <b style={{ fontWeight: "700" }}>
                        {displayData.activityText}
                      </b>
                    </p>
                  </div>
                </div>
              )}

              {/* DOG PERSONALITY TEXT */}
              {displayData.personalityList &&
                displayData.personalityList.length > 0 && (
                  <div className="bio-wrapper">
                    <span className="tag-icon">{displayData.tagIcon}</span>
                    <div className="feed-card text-card">
                      {displayData.personalityList.map((trait, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom:
                              index === displayData.personalityList.length - 1
                                ? 0
                                : "8px",
                          }}
                        >
                          <p className="bio-text" style={{ margin: 0 }}>
                            <b style={{ fontWeight: "700" }}>{trait}</b>
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* REMAINING PHOTOS */}
              {feedImages.slice(3).map((imgUrl, index) => (
                <React.Fragment key={index}>
                  <div className="feed-card photo-card">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "330px",
                        margin: "0 auto",
                      }}
                    >
                      <ImageWithPlaceholder
                        src={imgUrl}
                        placeholder={dogPlaceholder}
                        alt={`Feed item ${index + 4}`}
                        onLoadCallback={handleScrollLogic}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 16,
                          objectFit: "cover",
                        }}
                      />
                      <div className="feed-gradient-top" />
                      <div className="feed-gradient-bottom" />
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </>
          )}

          {/* Contact Card */}
          <div
            className="feed-card contact-card"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "1.5rem",
            }}
          >
            <div
              className="contact-item"
              style={{ flexDirection: "row", gap: "8px" }}
            >
              <MapPin size={22} />
              <span style={{ fontSize: "1rem" }}>
                {formatDistance(dog?.distance_miles)}
              </span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        {canScroll && (
          <div
            className={`scroll-indicator ${isBouncing ? "bouncing" : ""}`}
            onClick={handleScrollClick}
            style={{
              opacity: isBouncing ? 1 : 0.5,
            }}
          >
            <ArrowDown
              size={32}
              color="#fff"
              strokeWidth={3}
              style={{
                transform: isBouncing ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.5s ease",
              }}
            />
          </div>
        )}
      </div>

      {/* INCOMPLETE PROFILE MODAL */}
      {showIncompleteProfileModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div
            className="book-modal-card"
            style={{
              padding: "1.5rem",
              textAlign: "center",
              width: window.innerWidth >= 1024 ? "400px" : "",
              height: window.innerWidth >= 1024 ? "180px" : "",
            }}
          >
            <button
              className="modal-close-btn"
              onClick={() => setShowIncompleteProfileModal(false)}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <h2
              className="book-title"
              style={{
                marginTop: "1rem",
                marginBottom: "1rem",
                fontSize: "1rem",
              }}
            >
              Want to interact with the community? Complete your profile first!
              🐾
            </h2>

            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                className="profile-btn-half"
                onClick={() => {
                  setShowIncompleteProfileModal(false);
                  rememberCompletionReturn(profile?.id, { dogData: dog, activeView });
                  navigate("/team-selection", {
                    state: { routePath: "checkCompletionStatus" },
                  });
                }}
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TREAT CONFIRMATION MODAL */}
      {showTreatModal && treatDog && (
        <div className="treat-modal-overlay">
          <div className="treat-modal-card">
            <button
              className="treat-close-btn"
              onClick={handleCloseModal}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <div className="treat-modal-content">
              <div className="treat-icons-row">
                <div className="pro-treat-dog-avatar">
                  <ImageWithPlaceholder
                    src={displayData.images[0]}
                    placeholder={dogPlaceholder}
                    alt={treatDog.dog_name}
                  />
                </div>
                <img
                  src={tablerBone}
                  alt={"tablerBoneIcon"}
                  style={{ width: "60px" }}
                />
              </div>

              <h2 className="treat-text-main">
                You just tossed {displayData.name} a treat!
              </h2>
              <p className="treat-text-sub">
                Treats are limited to one per day per dog
              </p>

              <h3 className="treat-high-paw">HIGH PAW!</h3>
            </div>
          </div>
        </div>
      )}

      {/* OFFER A WALK MODAL */}
      {showBookWalkModal && treatDog && (
        <div className="modal-overlay">
          <div className="book-modal-card">
            <button
              className="modal-close-btn"
              onClick={handleCloseModal}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <div className="book-modal-header-icons">
              <div className="pro-treat-dog-avatar">
                <ImageWithPlaceholder
                  src={displayData.images[0]}
                  placeholder={dogPlaceholder}
                  alt={treatDog.dog_name}
                />
              </div>
            </div>

            <h2 className="book-title">When would you like to walk?</h2>
            <p className="book-subtitle">
              <b style={{ color: "#000" }}>Take the first step! 🐾</b>
              <br />
              Invite {getFirstName(
                treatDog?.user_name || treatDog?.dog_name,
              )}{" "}
              for a walk. Suggest a time or keep it flexible, you can work out
              the details together once they accept.
            </p>

            <div className="book-tabs">
              <span
                className={`book-tab ${walkTab === "calendar" ? "active" : ""}`}
                onClick={() => setWalkTab("calendar")}
              >
                Calendar
              </span>
              <span
                className={`book-tab ${walkTab === "flexible" ? "active" : ""}`}
                onClick={() => setWalkTab("flexible")}
              >
                I'm Flexible
              </span>
            </div>

            {walkTab === "calendar" && (
              <div className="tab-content-calendar">
                <div className="calendar-layout">
                  <div className="custom-calendar-container">
                    <div className="cal-header-info"></div>
                    <div className="cal-selected-display">
                      <h2>
                        {value ? format(value, "EEE, MMM d") : "Select a date"}
                      </h2>
                    </div>

                    <div className="rdp-wrapper-purple">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box
                          sx={{ width: "100%", maxWidth: 360 }}
                          onClick={handleCalendarClick}
                        >
                          <DateCalendar
                            value={value}
                            disablePast
                            onChange={(newValue) => {
                              setValue(newValue);
                              setSelectedTime(null);
                              handleScrollToTime();
                            }}
                          />
                        </Box>
                      </LocalizationProvider>
                    </div>
                  </div>

                  <div className="time-selector-area" ref={timeDropdownRef}>
                    <label>TIME</label>

                    <div
                      className="custom-dropdown"
                      style={{
                        backgroundColor: selectedTime
                          ? "#fff"
                          : "rgba(182, 252, 238, 1)",
                      }}
                      onClick={() => setShowTimeDropdown((p) => !p)}
                    >
                      <span>{selectedTime || "Choose a time"}</span>
                      <ChevronDown size={16} />
                    </div>

                    {showTimeDropdown && (
                      <div className="time-dropdown-list">
                        {TIME_SLOTS.map((time) => (
                          <div
                            key={time}
                            className={`time-option ${
                              selectedTime === time ? "active" : ""
                            }`}
                            onClick={() => {
                              setSelectedTime(time);
                              setShowTimeDropdown(false);
                            }}
                          >
                            {time}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="book-modal-next-container">
                      <button
                        className={`book-modal-next-btn ${!selectedTime ? "disabled" : ""}`}
                        disabled={!selectedTime}
                        onClick={() => {
                          const [time, meridian] = selectedTime.split(" ");
                          const [hour, minute] = time.split(":").map(Number);
                          const finalDateTime = value
                            .hour(
                              meridian === "PM" && hour !== 12
                                ? hour + 12
                                : hour === 12 && meridian === "AM"
                                  ? 0
                                  : hour,
                            )
                            .minute(minute)
                            .second(0);
                          setSelectedDate(finalDateTime);
                          openMeetingPoint();
                        }}
                      >
                        NEXT
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {walkTab === "flexible" && (
              <div className="tab-content-flexible">
                <div className="flexible-grid">
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("mornings")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("mornings")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={morningIcon}
                      alt="Mornings"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Mornings</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("afternoons")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("afternoons")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={afternoonIcon}
                      alt="Afternoon"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Afternoons</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("evenings")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("evenings")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={eveningIcon}
                      alt="Evening"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Evenings</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("weekdays")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("weekdays")}
                  >
                    <img
                      src={weekedaysIcon}
                      alt="Weekdays"
                      style={{
                        height: "40px",
                        width: "35px",
                        marginTop: "10px",
                      }}
                    />
                    <span>Weekdays</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("weekends")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("weekends")}
                    style={{ gap: "15px" }}
                  >
                    <img
                      src={weekenedsIcon}
                      alt="Weekends"
                      style={{
                        height: "35px",
                        width: "35px",
                        marginTop: "10px",
                      }}
                    />
                    <span>Weekends</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("vacation")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("vacation")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={vacationIcon}
                      alt="vacations"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Vacations</span>
                  </div>
                </div>
                <div className="book-modal-submit-container">
                  <button
                    className={`book-modal-submit-btn ${selectedFlexibleOptions.length === 0 ? "disabled" : ""}`}
                    disabled={selectedFlexibleOptions.length === 0}
                    onClick={handleFlexibleSubmit}
                  >
                    SUBMIT
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MEETING POINT MODAL */}
      {showMeetingPointModal && (
        <div className="meeting-point-overlay">
          <div className="meeting-point-card">
            <div className="meeting-point-header">
              <button className="mp-back-btn" onClick={backToBookWalk}>
                <ArrowLeft size={22} color="#000" strokeWidth={3} />
                <span style={{ color: "#000", fontWeight: "700" }}>Back</span>
              </button>
              <button
                className="mp-close-btn"
                onClick={closeAllModalsFully}
                style={{ zIndex: 999 }}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>

            <div className="meeting-point-content">
              <h2 className="mp-title">SELECT MEETING LOCATION</h2>
              <p className="mp-subtitle">
                Choose from common locations or tap on map to pin a location on
                map
              </p>

              <MeetingLocationPicker
                profile={profile}
                partnerId={treatDog?.user_id}
                dogId={((treatDog?.team_social === 1 && treatDog?.team_paws !== 1) || treatDog?.user_dog_status === 0) ? profile?.dogs?.[0]?.id : treatDog?.id}
                isMapLoaded={isMapLoaded}
                initialCenter={userLocation}
                selectedLocation={selectedMeetingLocation}
                selectedAddress={selectedMeetingAddress}
                onSelect={(place) => {
                  setSelectedMeetingLocation(place);
                  setSelectedMeetingAddress(place.address || place.name);
                }}
              />

              <button className="mp-confirm-btn" disabled={!hasMeetingLocation(selectedMeetingAddress)} onClick={openWalkConfirm}>
                NEXT: PERSONALIZE YOUR OFFER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WALK CONFIRM MODAL (Message) */}
      {showWalkConfirmModal && (
        <div className="walk-confirm-overlay">
          <div className="walk-confirm-card">
            <div className="walk-confirm-header">
              <button className="wc-back-btn" onClick={backToMeetingPoint}>
                <ArrowLeft size={22} color="#000" strokeWidth={3} />
                <span style={{ color: "#000", fontWeight: "700" }}>Back</span>
              </button>
              <button
                className="wc-close-btn"
                onClick={closeAllModalsFully}
                style={{ zIndex: 999 }}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>
            <div className="walk-confirm-content">
              <h2 className="wc-title">
                You would like to walk with {treatDog?.dog_name} &{" "}
                {treatDog?.user_name}{" "}
                {selectedDate ? (
                  `on ${dayjs(selectedDate).format("ddd, MMM D [at] h:mma")}`
                ) : (
                  <>
                    with <span>{selectedFlexibleOptions.join(", ")}</span>{" "}
                  </>
                )}
              </h2>

              <p className="wc-message-label">
                Personalize your offer with a message
              </p>
              <textarea
                className="wc-message-input"
                placeholder="Write here"
                value={walkMessage}
                onChange={(e) => setWalkMessage(e.target.value)}
              />

              <div className="wc-actions-row">
                <button
                  className="wc-btn confirm"
                  onClick={() => {
                    setShowWalkConfirmModal(false);
                    setShowSuccessModal(true);
                  }}
                >
                  NEXT: CONFIRM YOUR OFFER BEFORE SENDING
                </button>
              </div>
              <p className="wc-footer-text">
                We will reach out to {treatDog?.user_name} with your suggested
                time and find the best time for your first group walk to either
                confirm or suggest another time or place, check your bookings
                tab to see more!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal-card">
            <button
              className="modal-close-btn"
              onClick={closeAllModalsFully}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <h2 className="success-title">
              Your request to{" "}
              {getFirstName(treatDog?.user_name || treatDog?.dog_name)} is sent
            </h2>

            {walkMessage && (
              <div className="success-msg-preview">
                <p style={{ margin: "0 0 4px 0", fontWeight: "600" }}>
                  Your message:
                </p>
                <p style={{ margin: 0, fontStyle: "italic" }}>
                  "{walkMessage}"
                </p>
              </div>
            )}

            <h3 className="success-mp-title">Your Meeting point will be:</h3>
            <div className="success-map-box">
              <div className="success-map-placeholder">
                <div className="group-walk-pill">
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "#448a7b",
                      }}
                    ></div>{" "}
                    GROUP WALK
                  </span>
                </div>
                {isMapLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={selectedMeetingLocation || userLocation}
                    zoom={15}
                    options={{
                      disableDefaultUI: true,
                      gestureHandling: "none",
                    }}
                  >
                    <Marker
                      position={selectedMeetingLocation || userLocation}
                    />
                  </GoogleMap>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#e3f2fd",
                    }}
                  />
                )}
              </div>
              <div className="success-mp-details">
                <h4>
                  {selectedMeetingAddress
                    ? selectedMeetingAddress.split(",")[0]
                    : "Selected Location"}
                </h4>
                <p>
                  <MapPin size={14} color="#009bce" /> {selectedMeetingAddress}
                </p>
              </div>

              <div className="success-chips">
                <div className="success-chip blue">
                  <span>{getDynamicDateLabel(selectedDate)}</span>
                  <strong style={{ color: "#000" }}>
                    {selectedDate
                      ? dayjs(selectedDate).format("ddd, MMM D")
                      : selectedFlexibleOptions.join(", ")}
                  </strong>
                </div>
                {selectedDate && (
                  <div className="success-chip green">
                    <span>{getDynamicTimeLabel(selectedDate)}</span>
                    <strong style={{ color: "#000" }}>
                      {dayjs(selectedDate).format("h:mm A")}
                    </strong>
                  </div>
                )}
              </div>
            </div>
            <button
              className="success-done-btn"
              onClick={handleCreateWalk}
              disabled={isCreatingWalk}
            >
              {isCreatingWalk ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Loader2 className="animate-spin" size={18} />
                  <span>DONE</span>
                </div>
              ) : (
                "DONE"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDetailsPage;
